import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from '../data/data.service';
import { BaseComponent } from '../base/base.component';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../auth.service';
import { OrganizationService } from '../organization.service';
import { RedirectService } from '../redirect.service';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { SalesRep } from '../data/sales-rep';
import { PurchasedLicensesInfo } from '../data/purchased-licenses-info';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { VisibleAccount } from '../data/visible-account';

@Component({
    selector: 'app-organization-license-summary',
    templateUrl: './organization-license-summary.component.html',
    styleUrls: ['./organization-license-summary.component.css']
})
export class OrganizationLicenseSummaryComponent extends BaseComponent implements OnInit
{
    @ViewChild('content', { static: true }) modalContent: any;
    @Input() activeLicenses: PurchasedLicensesInfo[];
    @Input() orgSalesRep: {
        apmName: string;
        apmEmail: string;
    };
    @Input() organizationId: number;
    isWaiting: boolean = false;
    modalHeader: string;
    private modalService: NgbModal;
    private searchAccountResults: VisibleAccount[];
    searching = false;
    isAPMInherited: boolean = false;

    get activeLicenseCount(): number {
        if (!this.activeLicenses || !Array.isArray(this.activeLicenses)) {
            return 0;
        }
        return this.activeLicenses.reduce((sum, license) => sum + (license.Quantity || 0), 0);
    }

    get nextRenewalDate(): Date | null {
        if (!this.activeLicenses || !Array.isArray(this.activeLicenses)) {
            return null;
        }

        // First, try to find licenses where the organization is 'self'
        let relevantLicenses = this.activeLicenses.filter(license =>
            license.RelationshipToRequestedOrganization === 'self'
        );

        // If no 'self' licenses found, fall back to 'ancestor' licenses
        if (relevantLicenses.length === 0) {
            relevantLicenses = this.activeLicenses.filter(license =>
                license.RelationshipToRequestedOrganization === 'ancestor'
            );
        }

        if (relevantLicenses.length === 0) {
            return null;
        }

        const renewalDates = relevantLicenses
            .map(license => license.LicenseEndDate)
            .filter(date => date != null)
            .map(date => new Date(date));

        if (renewalDates.length === 0) {
            return null;
        }

        return new Date(Math.min(...renewalDates.map(date => date.getTime())));
    }

    emailExists() {
        return this.searchAccountResults != null && this.searchAccountResults.find(a => a.Email === this.orgSalesRep.apmEmail);
    }

    constructor(
        modalService: NgbModal,
        route: ActivatedRoute,
        authService: AuthService,
        organizationService: OrganizationService,
        redirectService: RedirectService,
        router: Router,
        dataService: DataService,
        location: Location
    )
    {
        super(route, authService, organizationService, redirectService, router, dataService, location);
        this.modalService = modalService;
    }

    ngOnInit()
    {
        this.modalHeader = this.orgSalesRep.apmName;
    }

    isInputValid(): boolean
    {
        return this.orgSalesRep.apmName &&
            this.orgSalesRep.apmEmail &&
            this.orgSalesRep.apmEmail.includes('@alegralearning.com');

    }

    searchAccounts = (text$: Observable<string>) =>
        text$.pipe(
            tap(term => this.searching = term.length > 2),
            debounceTime(200),
            distinctUntilChanged(),
            switchMap(term => {
                if (term.length > 2) {
                    return this.dataService.searchAccountsByEmail(term).pipe(
                        tap(items => {
                            this.searchAccountResults = items;
                        }),
                        map(items => items.filter(i => i.IsVisibileToRequestingAccount)),
                        map(va => va.map(v => v.Email))
                    );
                }
                else {
                    return of([]);
                }
            }),
            tap(() => this.searching = false),
        )

    accountLookupSelected(selectedAccountEmail: string) {
        this.orgSalesRep.apmEmail = selectedAccountEmail;
        // If the email exists, populate the name from the search results
        if (this.emailExists()) {
            const account = this.searchAccountResults.find(a => a.Email === selectedAccountEmail);
            if (account) {
                this.orgSalesRep.apmName = account.AccountName;
            }
        }
    }

    saveSalesRep(organizationId: number): void
    {
        this.isWaiting = true;
        const settingsData: SalesRep = {
            Name: this.orgSalesRep.apmName,
            Email: this.orgSalesRep.apmEmail
        };

        this.dataService.createOrUpdateOrganizationSalesRep(organizationId, settingsData).subscribe(
            response =>
            {
                this.isWaiting = false;
                this.modalService.dismissAll();
            },
            error =>
            {
                this.isWaiting = false;
                this.handleSaveError();
            }
        );
    }

    private fetchInheritedAPM(orgId: number, callback: (apmName: string, apmEmail: string) => void) {
        this.dataService.getOrganizationDetail(orgId).subscribe(
            orgDetail => {
                if (orgDetail && orgDetail.ParentOrganizationID) {
                    this.dataService.getOrganizationSalesRep(orgDetail.ParentOrganizationID).subscribe(
                        parentOrgArray => {
                            let parentApmName = '', parentApmEmail = '';
                            if (Array.isArray(parentOrgArray) && parentOrgArray.length > 0) {
                                const parentOrg = parentOrgArray[0];
                                parentApmName = parentOrg.Name || '';
                                parentApmEmail = parentOrg.EmailAddress || '';
                            }
                            if (parentApmName || parentApmEmail) {
                                callback(parentApmName, parentApmEmail);
                            } else {
                                // Recurse up the chain again
                                this.fetchInheritedAPM(orgDetail.ParentOrganizationID, callback);
                            }
                        },
                        error => {
                            callback('', '');
                        }
                    );
                } else {
                    callback('', '');
                }
            },
            error => {
                callback('', '');
            }
        );
    }

    openForView(activeLicenses: PurchasedLicensesInfo[], orgSalesRep: { apmName: string; apmEmail: string }, organizationId: number)
    {
        this.activeLicenses = activeLicenses;
        this.orgSalesRep = orgSalesRep;
        this.organizationId = organizationId;
        this.modalHeader = "License Summary";
        this.isAPMInherited = false;
        // If APM is missing, recursively fetch parent org's APM
        if (!orgSalesRep.apmName && !orgSalesRep.apmEmail) {
            this.fetchInheritedAPM(organizationId, (parentApmName, parentApmEmail) => {
                this.orgSalesRep = {
                    apmName: parentApmName,
                    apmEmail: parentApmEmail
                };
                this.isAPMInherited = !!(parentApmName || parentApmEmail);
                this.modalService.open(this.modalContent, { size: 'lg' });
            });
        } else {
            this.modalService.open(this.modalContent, { size: 'lg' });
        }
    }
}
